import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { companyApi } from '@/api';
import { Button, ErrorState, Icon, IconButton, SegmentedTabs } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

import {
  calendarRange,
  groupByDay,
  interviewSpan,
  isSameDay,
  rangeTitle,
  shiftAnchor,
  timeLabel,
} from './calendar';
import { hasOpenRequest } from './reschedule';
import { INTERVIEW_LABELS } from './status';

const MODES = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

/** Everything but cancelled -- a cancelled slot is free time, not a booking. */
const ACTIVE_STATUSES = ['SCHEDULED', 'COMPLETED', 'NO_SHOW'];

/** Chip colours per status, from the same tone tokens the badges use. */
const CHIP_TONES = {
  SCHEDULED: 'border-info-500 bg-info-50 text-info-700',
  COMPLETED: 'border-success-500 bg-success-50 text-success-700',
  NO_SHOW: 'border-danger-500 bg-danger-50 text-danger-700',
  CANCELLED: 'border-line bg-surface-muted text-ink-muted line-through',
};

/** How many chips fit in a month cell before it says "+N more". */
const MONTH_CELL_LIMIT = 3;

/**
 * The company's interviews laid out on a week or a month.
 *
 * It fetches its own window and nothing else: paging through months is a new
 * query per view, cached by react-query, so going back is instant. Choosing an
 * interview hands it to `onSelect`; what you can do with it belongs to the page,
 * which already owns the move, cancel and record-outcome dialogs.
 */
export function InterviewCalendar({ onSelect }) {
  const [mode, setMode] = useState('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [showCancelled, setShowCancelled] = useState(false);

  const { days, start, end } = useMemo(() => calendarRange(mode, anchor), [mode, anchor]);
  const statuses = showCancelled ? undefined : ACTIVE_STATUSES;

  const { data, isPending, isError, error, refetch } = useQuery({
    // Under `company-interviews`, so every mutation that already refreshes
    // the list refreshes the calendar too.
    queryKey: ['company-interviews', 'calendar', start.toISOString(), end.toISOString(), statuses],
    queryFn: () => companyApi.getInterviewCalendar({ start, end, statuses }),
    placeholderData: (previous) => previous,
  });

  const grouped = useMemo(() => groupByDay(data ?? [], days), [data, days]);
  const today = new Date();
  const total = data?.length ?? 0;

  function openWeekOf(day) {
    setAnchor(day);
    setMode('week');
  }

  return (
    <div>
      <div className="border-line flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <IconButton
            icon="arrowRight"
            className="rotate-180"
            label={mode === 'month' ? 'Previous month' : 'Previous week'}
            onClick={() => setAnchor((value) => shiftAnchor(mode, value, -1))}
          />
          <Button size="sm" variant="secondary" onClick={() => setAnchor(new Date())}>
            Today
          </Button>
          <IconButton
            icon="arrowRight"
            label={mode === 'month' ? 'Next month' : 'Next week'}
            onClick={() => setAnchor((value) => shiftAnchor(mode, value, 1))}
          />
          <h2 className="text-ink ml-1 text-base font-semibold tracking-tight" aria-live="polite">
            {rangeTitle(mode, anchor, days)}
          </h2>
          <span className="text-ink-muted text-xs">
            {total} {total === 1 ? 'interview' : 'interviews'}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-ink-muted flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              className="border-line accent-brand-600 size-4 rounded border"
              checked={showCancelled}
              onChange={(event) => setShowCancelled(event.target.checked)}
            />
            Show cancelled
          </label>
          <SegmentedTabs
            variant="plain"
            label="Calendar view"
            tabs={MODES}
            value={mode}
            onChange={setMode}
          />
        </div>
      </div>

      {isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : (
        <div className={cn('transition-opacity', isPending && 'opacity-60')} aria-busy={isPending}>
          {mode === 'month' ? (
            <MonthGrid
              grouped={grouped}
              month={anchor.getMonth()}
              today={today}
              onSelect={onSelect}
              onOpenDay={openWeekOf}
            />
          ) : (
            <WeekColumns grouped={grouped} today={today} onSelect={onSelect} />
          )}
        </div>
      )}
    </div>
  );
}

function WeekColumns({ grouped, today, onSelect }) {
  return (
    // Seven columns from a tablet up -- on a tablet held upright (iPad Mini to
    // iPad Pro) they stretch to fill the screen; a list of days on a phone,
    // where seven columns would be 40px each.
    <ol className="divide-line grid divide-y md:grid-cols-7 md:divide-x md:divide-y-0">
      {grouped.map(({ day, interviews }) => {
        const isToday = isSameDay(day, today);
        return (
          <li
            key={day.toISOString()}
            className="min-w-0 p-3 md:min-h-64 md:p-2 md:portrait:min-h-[calc(100dvh-17rem)] lg:p-3"
          >
            <p
              className={cn(
                'mb-2 flex items-baseline gap-1.5 text-xs',
                isToday ? 'text-brand-700 font-semibold' : 'text-ink-muted',
              )}
            >
              <span className="uppercase">
                {day.toLocaleDateString(undefined, { weekday: 'short' })}
              </span>
              <span
                className={cn('text-sm', isToday && 'bg-brand-600 rounded-full px-1.5 text-white')}
              >
                {day.getDate()}
              </span>
            </p>
            {interviews.length === 0 ? (
              <p className="text-ink-muted/70 text-xs md:hidden">No interviews</p>
            ) : (
              <ul className="space-y-1.5">
                {interviews.map((interview) => (
                  <li key={interview.id}>
                    <InterviewChip interview={interview} onSelect={onSelect} detailed />
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function MonthGrid({ grouped, month, today, onSelect, onOpenDay }) {
  const weekdays = grouped
    .slice(0, 7)
    .map(({ day }) => day.toLocaleDateString(undefined, { weekday: 'short' }));

  return (
    <div>
      <div className="border-line text-ink-muted grid grid-cols-7 border-b text-center text-[11px] uppercase">
        {weekdays.map((label) => (
          <span key={label} className="py-2">
            {label}
          </span>
        ))}
      </div>
      <ol className="grid grid-cols-7">
        {grouped.map(({ day, interviews }, index) => {
          const isToday = isSameDay(day, today);
          const outside = day.getMonth() !== month;
          const hidden = interviews.length - MONTH_CELL_LIMIT;
          const label = day.toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          });
          return (
            <li
              key={day.toISOString()}
              className={cn(
                'border-line min-h-16 min-w-0 border-b p-1 sm:min-h-28 sm:p-1.5',
                index % 7 !== 6 && 'border-r',
                outside && 'bg-surface-muted/50',
              )}
            >
              <button
                type="button"
                onClick={() => onOpenDay(day)}
                aria-label={`${label}: ${interviews.length} ${
                  interviews.length === 1 ? 'interview' : 'interviews'
                }. Open the week.`}
                className={cn(
                  'rounded-input mb-1 inline-flex size-6 items-center justify-center text-xs transition',
                  isToday
                    ? 'bg-brand-600 font-semibold text-white'
                    : outside
                      ? 'text-ink-muted/60 hover:bg-surface-muted'
                      : 'text-ink hover:bg-surface-muted',
                )}
              >
                {day.getDate()}
              </button>

              {/* A phone gets a count; there is no room for names. */}
              {interviews.length > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenDay(day)}
                  className="bg-info-50 text-info-700 block w-full rounded px-1 text-[11px] font-medium sm:hidden"
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  {interviews.length}
                </button>
              )}

              <ul className="hidden space-y-1 sm:block">
                {interviews.slice(0, MONTH_CELL_LIMIT).map((interview) => (
                  <li key={interview.id}>
                    <InterviewChip interview={interview} onSelect={onSelect} />
                  </li>
                ))}
              </ul>
              {hidden > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenDay(day)}
                  className="text-ink-muted hover:text-brand-700 mt-1 hidden text-[11px] sm:block"
                >
                  +{hidden} more
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function InterviewChip({ interview, onSelect, detailed = false }) {
  const { start, end } = interviewSpan(interview);
  const name = interview.candidate.full_name;
  const requested = hasOpenRequest(interview);
  const status =
    (INTERVIEW_LABELS[interview.status] ?? interview.status) +
    (requested ? ' · Reschedule requested' : '');

  return (
    <button
      type="button"
      onClick={() => onSelect(interview)}
      title={`${timeLabel(start)}–${timeLabel(end)} · ${name} · ${interview.title} (${status})`}
      className={cn(
        'block w-full min-w-0 rounded-md border-l-2 px-1.5 py-1 text-left text-[11px] leading-tight transition hover:brightness-95',
        CHIP_TONES[interview.status] ?? CHIP_TONES.SCHEDULED,
        requested && 'border-warning-500 bg-warning-50 text-warning-800',
      )}
    >
      {detailed ? (
        <>
          {/* Wraps rather than cuts off in a tablet-width column. */}
          <span className="block font-semibold tabular-nums">
            {timeLabel(start)}–{timeLabel(end)}
          </span>
          <span className="block truncate">
            {requested && <Icon name="repeat" size={10} className="mr-0.5 inline align-[-1px]" />}
            {name}
          </span>
          <span className="block truncate opacity-75">
            {interview.title} · {status}
          </span>
        </>
      ) : (
        <span className="block truncate">
          {requested && <Icon name="repeat" size={10} className="mr-0.5 inline align-[-1px]" />}
          <span className="font-semibold tabular-nums">{timeLabel(start)}</span> {name}
        </span>
      )}
      <span className="sr-only">, {status}</span>
    </button>
  );
}
